/*  ========================================================================
    # Components - Image Slider
    ========================================================================  */
import { AnimatePresence, MotionConfig, useInView } from 'motion/react';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import useResize from '../../hooks/useResize';
import useTranslation from '../../hooks/useTranslation';
import PaginatedSlider from '../PaginatedSlider';
import SingleSlide from './SingleSlide';
import PreloadImage from './PreloadImage';

const ImageSlider = ({
  startIndex = 0,
  images,
  aspectRatio = '',
  infinite,
  caption,
  fillParent = false
}) => {
  const __ = useTranslation();
  const sliderRef = useRef(null);
  const inView = useInView(sliderRef, { once: true, margin: '300px' });
  const [index, setIndex] = useState(startIndex);
  const [direction, setDirection] = useState(1);
  const [imageSize, setImageSize] = useState(0);

  useResize(sliderRef, entry => {
    setImageSize(entry.contentRect?.width);
  });

  const canGoBack = useMemo(() => index > 0 || infinite, [index, infinite]);
  const canGoNext = useMemo(() => index < images.length - 1 || infinite, [index, infinite, images]);

  const handleBack = useCallback(() => {
    if (!canGoBack) return;
    setDirection(-1);
    setIndex(prev => {
      if (prev <= 0) return images.length - 1;
      return prev - 1;
    });
  }, [images, setIndex, setDirection, canGoBack]);

  const handleNext = useCallback(() => {
    if (!canGoNext) return;
    setDirection(1);
    setIndex(prev => {
      if (prev >= images.length - 1) return 0;
      return prev + 1;
    });
  }, [images, setIndex, setDirection, canGoNext]);

  return (
    <MotionConfig reducedMotion='user'>
      <div className={`c-image-slider ${fillParent ? 'c-image-slider--fill-parent' : ''}`}>
        <div
          className='c-image-slider__wrapper'
          ref={sliderRef}
          style={fillParent ? null : { aspectRatio }}
          tabIndex={0}
          role='region'
          aria-label={__('images-slider-aria-title')}
          aria-roledescription='carousel'
          aria-live='polite'
          aria-atomic={false}
          onKeyDown={e => {
            if (e.code === 'ArrowLeft') handleBack();
            if (e.code === 'ArrowRight') handleNext();
          }}
        >
          <AnimatePresence initial={false} mode='sync' custom={direction}>
            <SingleSlide
              key={index}
              index={index}
              count={images.length}
              image={images[index]}
              imageFit={fillParent ? 'contain' : 'cover'}
              width={imageSize}
              direction={direction}
              caption={caption}
              inView={inView}
              canGoBack={canGoBack}
              canGoNext={canGoNext}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          </AnimatePresence>
          {inView && ( // Preload images for the slider
            <>
              <PreloadImage
                defaultSrc={images[(index + 1) % images.length].sizes.medium}
                srcSet={images[(index + 1) % images.length].srcSet}
                width={imageSize}
                inView={inView}
              />
              <PreloadImage
                defaultSrc={images[(index - 1 + images.length) % images.length].sizes.medium}
                srcSet={images[(index - 1 + images.length) % images.length].srcSet}
                width={imageSize}
                inView={inView}
              />
            </>
          )}
        </div>
        {images.length > 1 && (
          <div
            className={`c-image-slider__slider ${fillParent ? 'c-image-slider__slider--centered' : ''}`}
          >
            <PaginatedSlider
              index={index}
              count={images.length}
              infinite={infinite}
              onBack={handleBack}
              onNext={handleNext}
              backLabel={__('images-slider-button-back')}
              nextLabel={__('images-slider-button-next')}
            />
          </div>
        )}
      </div>
    </MotionConfig>
  );
};

ImageSlider.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      ID: PropTypes.number,
      alt: PropTypes.string,
      sizes: PropTypes.object, // No more detail here
      srcSet: PropTypes.string,
      url: PropTypes.string,
      title: PropTypes.string,
      caption: PropTypes.string,
      description: PropTypes.string
    })
  ),
  aspectRatio: PropTypes.string,
  fillParent: PropTypes.bool,
  infinite: PropTypes.bool,
  caption: PropTypes.bool,
  startIndex: PropTypes.number
};

export default ImageSlider;
